import React from "react";
import { Formik } from "formik";
import {
    TextField,
    Paper,
    withStyles,
    Button,
    Typography,
    CircularProgress,
    FormHelperText,
    InputLabel,
    Select, MenuItem, FormControl, Grid, capitalize, Box, FormControlLabel, Checkbox, FormGroup
} from "@material-ui/core";
import * as Yup from "yup";
import { BackButton, hasPermission } from '../../sc-utilities';

const styles = theme => ({
    root: {
        width: "auto",
        height: "auto",
        padding: theme.spacing(2),
        margin: theme.spacing(1)
    },
    submit: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.primary.main,
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        },
        width: "100%",
        marginTop: "10px"
    },
    formControl: {
        width: "100%"
    },
    closeButton: {
        color: theme.palette.buttonText.default,
        backgroundColor: theme.palette.error.dark,
        borderRadius: '1px'
    },
    example: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2)
    }
});

export const inchToMm = (inch) => (inch * 25.4).toFixed(2);

export const formats = [
    { value: 'zebra', label: 'Zebra' },
    { value: 'brother', label: 'Brother' }
];

const LabelTemplateFormFields = {
    Width: "PaperWidth",
    Height: "PaperHeight",
    Data: "Data",
    ProductType: "ProductType",
    Format: "Format",
    System: "System",
    Default: "Default",
    Name: "Name",
    Purpose: "Purpose",
}

export const { Width, Height, Data, ProductType, Format, System, Default, Name, Purpose } = LabelTemplateFormFields;

const LabelTemplateForm = props => {
    const { classes, formSettings, initialValues, handleSubmit, translations, permissions, isEdit, labelTemplate } = props;

    const hasCreateSystemLabelTemplatePermission = hasPermission(permissions, 'create_system_label_template');

    const productTypes = [
        { value: "PCProduct", label: translations['pc_product'] || 'PC Product' },
        { value: "MobileProduct", label: translations['mobile_product'] || 'Mobile Product' },
        { value: "StorageProduct", label: translations['storage_product'] || 'Storage Product' }
    ]

    const purposeTypes = [
        { value: "erasure", label: translations['erasure'] || 'Erasure' },
        { value: "diagnose", label: translations['diagnose'] || 'Diagnose' },
        { value: "combined", label: translations['combined'] || 'Combined' }
    ]

    return (
        <Paper className={classes.root}>
            {BackButton(translations)}
            <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                enableReinitialize={true}
                validationSchema={Yup.object().shape(formSettings.Validation)}
            >
                {formikProps => {
                    const { values, touched, errors, isValid, dirty, isSubmitting, setFieldValue, handleBlur, handleSubmit, handleChange } = formikProps;
                    const getDimensionField = (name, translation) => <Grid container direction="row" justify="center" alignItems="center" spacing={5}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                name={name}
                                value={values[name] || ""}
                                onBlur={handleBlur}
                                error={!!(errors[name] && touched[name])}
                                placeholder={translation}
                                label={`${translation} (in)`}
                                margin={"dense"}
                                fullWidth
                                type={"number"}
                                inputProps={{
                                    step: "0.1",
                                }}
                                onChange={(e) => setFieldValue(name, parseFloat(e.target.value))}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                name={name}
                                value={inchToMm(values[name]) || ""}
                                onBlur={handleBlur}
                                error={!!(errors[name] && touched[name])}
                                placeholder={translation}
                                label={`${translation} (mm)`}
                                margin={"dense"}
                                fullWidth
                                type={"number"}
                                inputProps={{
                                    step: "0.1",
                                }}
                                disabled
                            />
                        </Grid>
                    </Grid>

                    return (
                        <form onSubmit={handleSubmit}>
                            <Typography variant={"h6"}>{formSettings.FormTitle ? formSettings.FormTitle : null}</Typography>

                            {getDimensionField(Width, translations['paper_width'] || 'Paper width')}
                            {getDimensionField(Height, translations['paper_height'] || 'Paper height')}

                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations['select_product_type'] || "Please select product type"}</InputLabel>
                                <Select
                                    name={ProductType}
                                    label={translations["product_type"] || 'Product type'}
                                    margin={"dense"}
                                    value={values[ProductType] || ''}
                                    fullWidth
                                    onChange={handleChange}
                                >
                                    {(productTypes ?? []).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations['select_purpose'] || "Please select purpose"}</InputLabel>
                                <Select
                                    name={Purpose}
                                    label={translations["select_purpose"] || 'Purpose'}
                                    margin={"dense"}
                                    value={values[Purpose] || ''}
                                    fullWidth
                                    onChange={handleChange}
                                >
                                    {(purposeTypes ?? []).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl className={classes.formControl} margin={"dense"}>
                                <TextField
                                    name={Name}
                                    label={capitalize(translations['name'] || 'Name')}
                                    fullWidth
                                    value={values[Name] || ""}
                                    error={!!(errors[Name] && touched[Name])}
                                    onChange={handleChange}
                                />
                            </FormControl>

                            <FormControl className={classes.formControl} margin={"dense"}>
                                <TextField
                                    name={Data}
                                    label={capitalize(translations['data'] || 'Data')}
                                    multiline
                                    rows={10}
                                    fullWidth
                                    value={values[Data] || ""}
                                    onBlur={handleBlur}
                                    error={!!(errors[Data] && touched[Data])}
                                    onChange={handleChange}
                                />
                            </FormControl>

                            <FormHelperText className={classes.example}>{translations["example"] || "Example"}: 
                                {values[Format] === 'zebra' ? 
                                "^xa^cfa,50^fo100,100^fdHello World^fs^xz" : "<document><text-line>Hello world!</text-line></document>"}
                            </FormHelperText>
                            <Box className={classes.example}>
                                        <FormHelperText>
                                        {
                                            values[Format] === 'zebra' ?
                                                // eslint-disable-next-line no-template-curly-in-string
                                                translations["the_data_string_should_be_a_valid_ZPL_string"] || "The data string should be a valid ZPL string with placeholders to represent product data. Placeholders should be marked like so: ${PLACEHOLDER}." 
                                                :
                                                // eslint-disable-next-line no-template-curly-in-string
                                                translations["the_data_string_should_be_a_valid_XML_document"] || "The data string should be a valid XML document that uses custom tags. Placeholders should be marked like so: ${PLACEHOLDER}."
                                        }
                                        </FormHelperText>
                                        {
                                            values.ProductType === "PCProduct" &&
                                            <FormHelperText>{translations["available_placeholders"] || "Available placeholders"}: Vendor, Model, ModelShortened, Chassis, SerialNumber, LabelComment1,  LabelComment2,  LabelComment3,  LabelComment4, Notes, Grade, OS, SecurazeID, ProductID, OrderID, SaleLotID, SaleLotName, LotID, LotName, ExternalID, WipeStatus, VerificationStatus, QRCodeProductID, QRCodeProductIDLength, Date, DateUK, DateUS{ values.Purpose !== "erasure" && ', DiagnoseResult, DiagnoseResultSummary, DiagnoseDate' }, CreatedAt, TypicalProblems, Configuration, SMARTOverall, SMARTHealth, StorageSize, StorageSerial, BatteryHealth, BatteryCapacity, BatteryCycles, SecurazeUser, DataOrigin, MDM, ANumber, DEP, FindMyMac, RAM, Resolution, ScreenSize, SKU, ProcessorClock, Processor[1-n] (e.g. Processor1), Processor (all processors separated by /), GPU (all GPUs separated by /)</FormHelperText>
                                        }
                                        {
                                            values.ProductType === "MobileProduct" &&
                                            <>
                                                <FormHelperText>{translations["available_placeholders"] || "Available placeholders"}: Vendor, Model, ModelShortened, AdditionalModelInfo, Chassis, SerialNumber, IMEI, IMEI2, LabelComment1,  LabelComment2,  LabelComment3,  LabelComment4, Notes, Grade, OS, SecurazeID, ProductID, OrderID, SaleLotID, LotID, LotName, ExternalID, WipeStatus, VerificationStatus, QRCodeProductID, QRCodeProductIDLength, Date, DateUK, DateUS{ values.Purpose !== "erasure" && ', DiagnoseResult, DiagnoseResultSummary, DiagnoseDate' }, CreatedAt, TypicalProblems, Configuration, SMARTOverall, SMARTHealth, StorageSize, StorageSerial, BatteryHealth, BatteryCapacity, BatteryCycles, SecurazeUser, DataOrigin, InternalName, ANumber, FindMyIphoneFRP, Carrier, CarrierLock, Financed, MDM, SimLock, Stolen, WarrantyStatus, RepairsAndServiceCoverage, RepairsAndServiceExpirationDate, AppleCareEligible, EstimatedPurchaseDate, ReplacedDevice, LoanerDevice, OperatingSystemVersion, OperatingSystemVersionPreProcessing, Processor, DeviceColor, ECID, MEID, ProductVersion, NetworkStandard, AvailableSIMCardSlots, InstalledSIMCards, SDCardAmount, DisplaySize, IsRooted, PortName, WipeFinishedDay, WipeFinishedDayUS, WipeFinishedDayUK, WipeFinishedDayDE, SKU</FormHelperText>
                                            </>
                                        }
                                        { values.Purpose !== "erasure" && <FormHelperText>DiagnoseStatuses|[initialX]|[initialY]|[numberOfColumns]|[offsetColumns]|succeeded;failed;aborted;notPerformed;notSupported|maxNumberOfRows|maxNumberOfTestCases|orderCases(Alphabetical|FailedFirst)</FormHelperText>}
                            </Box>

                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["please_select_format"] || "Please select format"}</InputLabel>
                                <Select
                                    name={Format}
                                    label={translations["format"] || 'Format'}
                                    margin={"dense"}
                                    value={values[Format] || ''}
                                    fullWidth
                                    onChange={handleChange}
                                >
                                    {(formats ?? []).map(option => (
                                        <MenuItem key={option.value} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {hasCreateSystemLabelTemplatePermission && !isEdit && <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color={'primary'}
                                            margin={"dense"}
                                            name={System}
                                            checked={!!values[System]}
                                            onChange={handleChange}
                                        />
                                    }
                                    label={translations['system'] || 'System'}
                                />
                            </FormGroup>}
                            
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color={'primary'}
                                            margin={"dense"}
                                            name={Default}
                                            checked={!!values[Default]}
                                            onChange={handleChange}
                                        />
                                    }
                                    label={translations['default'] || 'Default'}
                                />
                            </FormGroup>

                            <div>
                                {formSettings.ButtonSubmit && (isEdit ? !labelTemplate?.["CustomerID"] ? hasCreateSystemLabelTemplatePermission : true : true) ? (
                                    <Button
                                        fullWidth={true}
                                        className={classes.submit}
                                        variant="contained"
                                        color={formSettings.ButtonSubmit.Color}
                                        type="submit"
                                        style={{ color: isSubmitting || !(isValid && dirty) ? null : 'white' }}
                                        disabled={isSubmitting || !(isValid && dirty)}
                                    >
                                        {isSubmitting ? <CircularProgress size={24} /> : formSettings.ButtonSubmit.Title}
                                    </Button>
                                ) : null}
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </Paper>
    );
};
export default withStyles(styles, { withTheme: true })(LabelTemplateForm);
